/**
 * WebSocket Notification Client (Web Platform Adapters §8)
 *
 * Connects to the CryptoBlobStore WS endpoint, subscribes to blobId changes,
 * and triggers a sync callback when head-changed events arrive.
 * Auto-reconnects with exponential backoff (1s → 30s cap).
 *
 * Spec §7: When per-blob auth providers are supplied, subscribe messages include
 * an accessToken obtained via challenge-signing (Spec §7.2).
 */

import type { WorkspaceAuthProvider } from './workspaceSecretV2'

export interface WsNotificationCallbacks {
    onHeadChanged: (blobId: string, head: string, lane?: string | null) => void
    onConnectionChange: (connected: boolean) => void
}

export class WsNotificationClient {
    private ws: WebSocket | null = null
    private subscribedTargets = new Set<string>()
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null
    private backoffMs = 1000
    private disposed = false
    private readonly wsUrl: string

    constructor(
        private readonly apiBaseUrl: string,
        private readonly callbacks: WsNotificationCallbacks,
        private readonly resolveAuthProvider?: (blobId: string) => WorkspaceAuthProvider | undefined,
    ) {
        this.wsUrl = apiBaseUrl.replace(/^http/, 'ws') + '/ws'
    }

    connect(): void {
        if (this.disposed) return

        try {
            this.ws = new WebSocket(this.wsUrl)
        } catch {
            this.scheduleReconnect()
            return
        }

        this.ws.onopen = () => {
            this.backoffMs = 1000
            this.callbacks.onConnectionChange(true)

            // Re-subscribe all tracked blobIds
            this.resubscribeAll()
        }

        this.ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data as string)
                if (msg.type === 'head-changed' && msg.blobId && msg.head) {
                    this.callbacks.onHeadChanged(msg.blobId, msg.head, typeof msg.lane === 'string' ? msg.lane : null)
                } else if (msg.type === 'ping') {
                    this.send({ type: 'pong' })
                }
            } catch {
                // Invalid JSON — ignore per spec
            }
        }

        this.ws.onclose = () => {
            this.callbacks.onConnectionChange(false)
            this.scheduleReconnect()
        }

        this.ws.onerror = () => {
            // onclose will fire after onerror
        }
    }

    subscribe(blobId: string, lane?: string | null): void {
        this.subscribedTargets.add(this.subscriptionKey(blobId, lane))
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.sendSubscribe(blobId, lane)
        }
    }

    unsubscribe(blobId: string, lane?: string | null): void {
        this.subscribedTargets.delete(this.subscriptionKey(blobId, lane))
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.send({ type: 'unsubscribe', blobId, ...(lane != null ? { lane } : {}) })
        }
    }

    dispose(): void {
        this.disposed = true
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
        if (this.ws) {
            this.ws.onclose = null
            this.ws.onerror = null
            this.ws.close()
            this.ws = null
        }
    }

    get isConnected(): boolean {
        return this.ws?.readyState === WebSocket.OPEN
    }

    /**
     * Send a subscribe message for a single blobId.
     * Spec §7.2 / §1.2: v2 requires an accessToken; no legacy format is supported.
     * When no auth provider is available for the blobId, subscribe is silently skipped.
     */
    private sendSubscribe(blobId: string, lane?: string | null): void {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return

        const authProvider = this.resolveAuthProvider?.(blobId)

        if (!authProvider) {
            // No auth provider — cannot subscribe without v2 access token (Spec §1.2)
            return
        }

        // Async: get token then send subscribe
        authProvider.getAccessToken('read')
            .then(token => {
                this.send({
                    type: 'subscribe',
                    blobId,
                    ...(lane != null ? { lane } : {}),
                    accessToken: token.token,
                })
            })
            .catch(() => {
                // Token acquisition failed — skip this subscription
                // Reconnect loop will retry
            })
    }

    /**
     * Re-subscribe to all tracked blobIds after reconnect.
     * Spec §7.2: each subscribe includes a fresh accessToken.
     */
    private resubscribeAll(): void {
        if (this.subscribedTargets.size === 0) return
        for (const target of this.subscribedTargets) {
            const [blobId, lane] = this.parseSubscriptionKey(target)
            this.sendSubscribe(blobId, lane)
        }
    }

    private send(msg: Record<string, unknown>): void {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(msg))
        }
    }

    private scheduleReconnect(): void {
        if (this.disposed) return
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer)

        this.reconnectTimer = setTimeout(() => {
            this.connect()
        }, this.backoffMs)

        // Exponential backoff: 1s → 2s → 4s → 8s → 16s → 30s cap
        this.backoffMs = Math.min(this.backoffMs * 2, 30_000)
    }

    private subscriptionKey(blobId: string, lane?: string | null): string {
        return lane == null ? blobId : `${blobId}|lane|${lane}`
    }

    private parseSubscriptionKey(key: string): [string, string | null] {
        const marker = '|lane|'
        const markerIndex = key.indexOf(marker)
        if (markerIndex < 0) return [key, null]
        return [key.slice(0, markerIndex), key.slice(markerIndex + marker.length)]
    }
}
